import { Button, SplitButton } from "@landtechnologies/components";
import { SaveSiteLayer } from "../../typings";
import { LogEventCreatedSiteEventLocation } from "react-migration/lib/util/logEventV2";
import { useEffect, useState } from "react";
import { Stores, useGlobalState, useTranslation } from "react-migration/lib";
import { McSearchResultDTO } from "@landtechnologies/dtos";
import { StageSelect } from "./StageSelect";
import { useInitialInformationForResults } from "./useDryRunSitesForResults";
import { useSaveSitesBulk } from "./useSaveSitesBulk";
import { useChangeSiteStage } from "./useChangeSiteStage";

type SaveSiteBulkButtonProps = {
  currentPageResults: McSearchResultDTO[];
  layer: SaveSiteLayer;
  source: LogEventCreatedSiteEventLocation;
};

export const SaveSiteBulkButton = ({
  currentPageResults,
  layer,
  source,
}: SaveSiteBulkButtonProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [, { setResultsSaved }] = useGlobalState(Stores.search);

  const {
    allSitesAreSaved,
    geometries,
    isLoading: isInitialLoading,
  } = useInitialInformationForResults({
    layer,
    currentPageResults,
  });
  const {
    loading: isStagesLoading,
    networkStatus,
    stagesOrdered,
    selectedStageId,
    handleChangeStage,
  } = useChangeSiteStage();

  useEffect(() => {
    setResultsSaved(false);
  }, [currentPageResults]);

  const {
    bulkSaveProcessDone,
    isLoading: isSavingLoading,
    handleSaveBulk,
  } = useSaveSitesBulk({
    geometries,
    layer,
    source,
    selectedStageId,
  });

  return (
    <SplitButton
      classNameMainButton="atlas-h-9 atlas-font-semibold"
      disabled={isStagesLoading || allSitesAreSaved || bulkSaveProcessDone}
      onClick={handleSaveBulk}
      text={
        allSitesAreSaved || bulkSaveProcessDone
          ? t("sidebar.react.saved_search_sidebar.saved")
          : t("sidebar.react.search_sidebar.save_results")
      }
      isModalOpen={isOpen}
      setIsModalOpen={setIsOpen}
      classNameModal="atlas-w-60"
      loading={isInitialLoading || isSavingLoading}
    >
      <StageSelect
        size="xs"
        value={selectedStageId}
        onChange={handleChangeStage}
        stages={stagesOrdered || []}
        loading={isStagesLoading}
        networkStatus={networkStatus}
      />
      <div className="atlas-flex atlas-justify-between atlas-mt-4">
        <Button.Borderless
          size="small"
          className="atlas-px-0 atlas-font-normal"
          onClick={() => setIsOpen(false)}
        >
          {t("sidebar.react.saved_search_sidebar.cancel")}
        </Button.Borderless>
        <Button.Primary
          size="small"
          className="atlas-px-6 atlas-bg-[#ee64b3] focus:atlas-bg-[#d0529a] hover:atlas-bg-[#d0529a] atlas-font-normal"
          onClick={handleSaveBulk}
          data-testid="save-site-inner"
        >
          {t("sidebar.react.saved_search_sidebar.save")}
        </Button.Primary>
      </div>
    </SplitButton>
  );
};
